import { useNavigate } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";

import { TemplateRequestModal } from "@/components/upgrade-modals/template-request-modal";
import { UnlockModal } from "@/components/upgrade-modals/unlock-modal";
import { ChangeAccountModal } from "@/features/request/edit/components/header/modals/ChangeAccountModal";
import { ChangeRequestTypeModal } from "@/features/request/edit/components/header/modals/ChangeRequestTypeModal";
import { DeleteRequestModal } from "@/features/request/edit/components/header/modals/DeleteRequestModal";
import { DuplicateRequestModal } from "@/features/request/edit/components/header/modals/DuplicateRequestModal";
import { HavingProblemsModal } from "@/features/request/edit/components/header/modals/HavingProblemsModal";
import { TogglePauseModal } from "@/features/request/edit/components/header/modals/TogglePauseModal";
import { RatingModal } from "@/features/request/edit/components/rating-modal";
import { SuggestionModal } from "@/features/request/edit/components/suggestion-modal";
import { useSuggestions } from "@/features/request/edit/hooks/useSuggestions";
import { useDuplicateTemplate } from "@/features/templates/hooks/useDuplicateTemplate.tsx";

export type EditRequestModalsContextValue = {
  closeAll: () => void;
  openChangeAccount: () => void;
  openChangeType: () => void;
  openCompletionFeedback: () => void;
  openDeleteModal: () => void;
  openDuplicateRequest: () => void;
  openHavingProblems: () => void;
  openSuggestion: () => void;
  openTemplateInfo: () => void;
  openTogglePauseConfirm: () => void;
  openUnlock: () => void;
};

const EditRequestModalsContext =
  createContext<EditRequestModalsContextValue | null>(null);

export function useEditRequestModals(): EditRequestModalsContextValue {
  const ctx = useContext(EditRequestModalsContext);

  if (!ctx)
    throw new Error(
      "useEditRequestModals must be used within EditRequestModalsProvider",
    );

  return ctx;
}

export function EditRequestModalsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [problemsOpen, setProblemsOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [changeAccountOpen, setChangeAccountOpen] = useState(false);
  const [changeTypeOpen, setChangeTypeOpen] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [togglePauseConfirmOpen, setTogglePauseConfirmOpen] = useState(false);
  const [templateInfoOpen, setTemplateInfoOpen] = useState(false);

  const suggestions = useSuggestions();
  const { handleCreateDraft } = useDuplicateTemplate();
  const navigate = useNavigate();

  const handleTemplateDuplicate = () => {
    setTemplateInfoOpen(false);
    void handleCreateDraft();
  };

  const handleTemplateCreateNew = () => {
    setTemplateInfoOpen(false);
    navigate({ to: "/requests/pick-type" });
  };

  const value = useMemo(
    () => ({
      closeAll: () => {
        setDeleteOpen(false);
        setProblemsOpen(false);
        setDuplicateOpen(false);
        setChangeAccountOpen(false);
        setChangeTypeOpen(false);
        setCompletionOpen(false);
        setSuggestionOpen(false);
        setUnlockOpen(false);
        setTemplateInfoOpen(false);
        setTogglePauseConfirmOpen(false);
      },
      openChangeAccount: () => setChangeAccountOpen(true),
      openChangeType: () => setChangeTypeOpen(true),
      openCompletionFeedback: () => setCompletionOpen(true),
      openDeleteModal: () => setDeleteOpen(true),
      openDuplicateRequest: () => setDuplicateOpen(true),
      openHavingProblems: () => setProblemsOpen(true),
      openSuggestion: () => setSuggestionOpen(true),
      openTemplateInfo: () => setTemplateInfoOpen(true),
      openTogglePauseConfirm: () => setTogglePauseConfirmOpen(true),
      openUnlock: () => setUnlockOpen(true),
    }),
    [],
  );

  return (
    <EditRequestModalsContext.Provider value={value}>
      {children}
      <DeleteRequestModal
        isOpen={deleteOpen}
        onOpenChange={setDeleteOpen}
        onOpenUnlock={() => setUnlockOpen(true)}
      />
      <HavingProblemsModal
        isOpen={problemsOpen}
        onOpenChange={setProblemsOpen}
        onOpenUnlock={() => setUnlockOpen(true)}
      />
      <DuplicateRequestModal
        isOpen={duplicateOpen}
        onOpenChange={setDuplicateOpen}
      />
      <ChangeAccountModal
        isOpen={changeAccountOpen}
        onOpenChange={setChangeAccountOpen}
      />
      <ChangeRequestTypeModal
        isOpen={changeTypeOpen}
        onOpenChange={setChangeTypeOpen}
        onOpenUnlock={() => setUnlockOpen(true)}
      />
      <RatingModal
        isOpen={completionOpen}
        onOpenChange={setCompletionOpen}
        onSubmitted={() => {
          if (suggestions.suggestions.length === 3) {
            setSuggestionOpen(true);
          }
        }}
      />
      <SuggestionModal
        isOpen={suggestionOpen}
        onOpenChange={setSuggestionOpen}
        suggestions={suggestions.suggestions}
        title={suggestions.title}
      />
      <UnlockModal isOpen={unlockOpen} onOpenChange={setUnlockOpen} />
      <TemplateRequestModal
        isOpen={templateInfoOpen}
        onCreateNew={handleTemplateCreateNew}
        onDuplicate={handleTemplateDuplicate}
        onOpenChange={setTemplateInfoOpen}
      />
      <TogglePauseModal
        isOpen={togglePauseConfirmOpen}
        onOpenChange={setTogglePauseConfirmOpen}
      />
    </EditRequestModalsContext.Provider>
  );
}
